<template>
  <v-modal
    ref="templateModal"
    :classes="'auth-ui-modal is-wide'"
  >
    <span slot="header-title">{{ $t('admin.magicLinkTemplateModalTitle') }}</span>
    <div slot="body">
      <div class="auth-ui-column-container is-left-aligned has-margin-bottom">
        <span>{{ $t('admin.magicLinkTemplateModalDescription') }}
          <span v-pre>{{ magicLinkUrl }}</span>.
        </span>
      </div>

      <div class="auth-ui-column-container is-left-aligned">
        <!-- Filter templates -->
        <div class="field">
          <label class="label">
            {{ $t('admin.magicLinkTemplateModalFilterTitle') }}
          </label>
          <div class="control has-icons-left">
            <input
              v-model="searchFieldValue"
              class="input"
              type="text"
              placeholder="E.g. name or ID"
              @keyup="filterTemplates()"
            >
            <span class="icon is-small is-left">
              <i
                class="fa fa-search"
                aria-hidden="true"
              />
            </span>
          </div>
          <div class="auth-ui-modal-template-filter-result">
            {{ $t('admin.magicLinkTemplateModalFilterResults') }} {{ filteredTemplates.length }}
          </div>
        </div>

        <!-- Template list with sorting headers -->
        <div class="field is-full-width">
          <label class="label auth-ui-select-label">
            <span class="auth-ui-select-wide-screen-label">
              <div class="auth-ui-modal-template-caret-container">
                {{ $t('admin.magicLinkTemplateModalListName') }}
                <i
                  class="fa fa-caret-down"
                  @click.prevent="sortBy('name', 'down')"
                />
                <i
                  class="fa fa-caret-up"
                  @click.prevent="sortBy('name', 'up')"
                />
              </div>
              <div class="auth-ui-modal-template-caret-container">
                {{ $t('admin.magicLinkTemplateModalListId') }}
                <i
                  class="fa fa-caret-down"
                  @click.prevent="sortBy('id', 'down')"
                />
                <i
                  class="fa fa-caret-up"
                  @click.prevent="sortBy('id', 'up')"
                />
              </div>
              <div class="auth-ui-modal-template-caret-container">
                {{ $t('admin.magicLinkTemplateModalListUpdated') }}
                <i
                  class="fa fa-caret-down"
                  @click.prevent="sortBy('updated_at', 'down')"
                />
                <i
                  class="fa fa-caret-up"
                  @click.prevent="sortBy('updated_at', 'up')"
                />
              </div>
            </span>
            <span class="auth-ui-select-narrow-screen-modal-label">
              <div class="auth-ui-template-modal-narrow-screen-label-with-select">
                <div>{{ $t('admin.magicLinkTemplateModalNarrowListSortBy') }}:</div>
                <div class="select auth-ui-tabs-select-fit-content has-left-padding">
                  <select
                    id="tab-select"
                    @change="selectSortByMethod($event)"
                  >
                    <option
                      v-for="method in sortMethods"
                      :key="'select-' + method"
                      :value="method"
                    >
                      {{ $t(`admin.magicLinkTemplateModalNarrowList${method}`) }}
                    </option>
                  </select>
                </div>
                <div
                  class="auth-ui-modal-template-caret-container"
                >
                  <i
                    :class="{ 'fa-caret-up': selectedSortDirection === 'up',
                              'fa-caret-down': selectedSortDirection === 'down'}"
                    class="fa has-left-padding"
                    @click.prevent="narrowSortBy()"
                  />
                </div>
              </div>
            </span>
          </label>
          <div class="control">
            <div class="select is-multiple auth-ui-card-select">
              <div
                class="auth-ui-modal-select-box"
              >
                <div
                  v-for="(template, index) in filteredTemplates"
                  :id="template.name"
                  :key="template.id + '-template-' + index"
                  :class="{
                    'auth-ui-select-option--is-selected': template.id === selectedTemplate.id
                  }"
                  class="auth-ui-select-option"
                  @click="setSelectedTemplate(template)"
                >
                  <div class="auth-ui-row-container auth-ui-select-option-container">
                    <div class="auth-ui-select-option-narrow-header">
                      {{ $t('admin.magicLinkTemplateModalNarrowListName') }}:
                    </div>
                    <div
                      :title="template.name"
                      class="auth-ui-select-text-row"
                    >
                      {{ template.name }}<span v-if="template.defaultTemplate">,
                        (default)</span>
                    </div>
                  </div>

                  <div class="auth-ui-row-container auth-ui-select-option-container">
                    <div class="auth-ui-select-option-narrow-header">
                      {{ $t('admin.magicLinkTemplateModalNarrowListId') }}:
                    </div>
                    <div
                      :title="template.id"
                      class="auth-ui-select-text-row"
                    >
                      {{ template.id }}
                    </div>
                  </div>

                  <div
                    class="auth-ui-row-container auth-ui-select-option-container
                     is-last-with-set-width"
                  >
                    <div class="auth-ui-select-option-narrow-header">
                      {{ $t('admin.magicLinkTemplateModalNarrowListUpdated') }}:
                    </div>
                    <div
                      :title="template.updated_at"
                      class="auth-ui-select-text-row"
                    >
                      {{ translateDate(template.updated_at) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Refresh templates and select template buttons -->
      <div class="field has-tiny-margin-top">
        <div class="control auth-ui-row-container">
          <div
            class="auth-ui-refresh-container"
            @click.prevent="handleRefreshTemplates()"
          >
            {{ $t('admin.magicLinkTemplateModalUpdateList') }}
            <i
              :title="$t('admin.magicLinkTemplateModalTooltip')"
              :class="{ 'auth-ui-template-select-refresh--is-spinning': !templateRefreshReady}"
              class="fa fa-refresh auth-ui-template-select-refresh"
            />
          </div>
          <button
            class="button is-link auth-ui-pulled-right"
            @click.prevent="handleSelect(selectedTemplate)"
          >
            {{ $t('admin.magicLinkTemplateModalSelectButton') }}
          </button>
        </div>
      </div>
    </div>
  </v-modal>
</template>

<script>
/* ============
     * Template Selection Modal Component
     * ============
     *
     * Shows the list of available dynamic templates for mail sending.
     *
     * Used by the tabs of the "Mail Sends" component.
     *
     */
import TimeTool from '../../../../tools/TimeTool';
import VModal from '../../../../components/Modal.vue';

export default {
  name: 'TemplateSelectionModal',

  components: {
    VModal,
  },

  props: {
    templates: {
      type: Array,
      default: () => [],
    },
    refreshTemplates: {
      type: Function,
      default: () => {},
    },
    templateRefreshReady: {
      type: Boolean,
      default: false,
    },
    selectTemplate: {
      type: Function,
      default: () => {},
    },
  },

  data() {
    return {
      filteredTemplates: [],
      selectedTemplate: {
        name: '',
        id: '',
        updated_at: '',
      },
      searchFieldValue: '',
      selectedSortMethod: 'name',
      selectedSortDirection: 'up',
      sortMethods: ['Name', 'Id', 'Updated'],
    };
  },

  watch: {
    /**
     * Updates the templates inside this component when the parent has finished loading
     * the template list.
     */
    templateRefreshReady(newVal) {
      if (newVal) {
        this.filteredTemplates = this.templates;
      }
    },
  },

  methods: {
    /**
     * Closes/opens this modal.
     */
    toggleModal() {
      this.$forceUpdate(); // needed to update data()
      this.filteredTemplates = this.templates;
      this.setSelectedTemplate(this.filteredTemplates[0]);
      this.$refs.templateModal.toggleModal();
    },

    /**
     * Colorizes the currently selected row and sets the selected template inside this component.
     * The selection does not affect the parent's selected component before handleSelect is called.
     */
    setSelectedTemplate(template) {
      this.selectedTemplate = template;
    },

    /**
     * Sets the selected template using a parent component's method given vie props.
     * Also closes this modal.
     */
    handleSelect() {
      this.selectTemplate(this.selectedTemplate);
      this.$refs.templateModal.toggleModal();
    },

    /**
     * Filters the list of templates by user-inputted text.
     */
    filterTemplates() {
      const filter = this.searchFieldValue.toUpperCase();
      const filteredTemplates = [];

      this.templates.forEach((template) => {
        this.filteredTemplates = this.templates;

        if (template.name.toUpperCase().indexOf(filter) > -1
          || template.id.toUpperCase().indexOf(filter) > -1
          || template.updated_at.toUpperCase().indexOf(filter) > -1) {
          filteredTemplates.push(template);
        }
      });

      this.filteredTemplates = filteredTemplates;
    },

    /**
     * Sorts an array of objects the given key value.
     */
    sortBy(key, direction) {
      const name = key.toLowerCase();
      let sortValueA = -1;
      let sortValueB = 1;

      if (direction === 'up') {
        sortValueA = 1;
        sortValueB = -1;
      }

      this.filteredTemplates.sort(
        (a, b) => (
          // eslint-disable-next-line no-nested-ternary
          (a[name] > b[name]) ? sortValueA : ((b[name] > a[name]) ? sortValueB : 0)
        ),
      );
    },

    /**
     * Calls the refresh method given via props.
     */
    async handleRefreshTemplates() {
      if (this.templateRefreshReady) {
        this.searchFieldValue = '';
        this.filteredTemplates = [];
        await this.refreshTemplates();
      }
    },

    /**
     * Translates the given date with the correct locale and options.
     */
    translateDate(time) {
      return TimeTool.translateDate(time);
    },

    /**
     * Sets which key name should be used for sorting the template list for narrow screens
     */
    selectSortByMethod(event) {
      let value = event.target.value;

      if (value.toLowerCase() === 'updated') {
        value = 'updated_at';
      }
      this.selectedSortMethod = value;
    },

    narrowSortBy() {
      this.selectedSortDirection = this.selectedSortDirection === 'up' ? 'down' : 'up';
      this.sortBy(this.selectedSortMethod, this.selectedSortDirection);
    },
  },
};
</script>
